import React, { useState } from 'react';
import {
  Download,
  FileText,
  Hash,
  Copy,
  CheckCircle,
  AlertCircle,
  Info,
  HelpCircle,
  Image as ImageIcon,
  Upload,
  RefreshCw,
  ArrowRight,
  FileSpreadsheet,
  FolderOpen
} from 'lucide-react';

const UUIDHelper = () => {
  const [questionCount, setQuestionCount] = useState(10);
  const [generatedUUIDs, setGeneratedUUIDs] = useState([]);
  const [activeTab, setActiveTab] = useState('workflow');
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Generate UUIDs using crypto.randomUUID()
  const generateUUIDs = () => {
    const uuids = [];
    for (let i = 0; i < questionCount; i++) {
      uuids.push({
        questionNumber: i + 1,
        uuid: crypto.randomUUID()
      });
    }
    setGeneratedUUIDs(uuids);
  };



  // Download file function
  const downloadFile = (content, filename, type = 'text/plain') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download UUID list as Excel template
  const downloadExcelWithUUIDs = async () => {
    try {
      // Get the Excel template from the backend
      const response = await fetch('/api/super-admin/questions/excel-template', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `question_template_with_uuids_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download Excel template:', err);
      // Fallback to simple UUID list
      downloadUUIDList();
    }
  };

  // Download simple UUID list
  const downloadUUIDList = () => {
    const content = generatedUUIDs.map(item => `${item.questionNumber}\t${item.uuid}`).join('\n');
    const header = 'Question Number\tUUID\n';
    downloadFile(header + content, `question_uuids_${Date.now()}.txt`, 'text/plain');
  };



  // Download comprehensive guide
  const downloadCompleteGuide = () => {
    let guide = `BULK UPLOAD COMPLETE GUIDE\n`;
    guide += `===========================\n\n`;
    guide += `Generated on: ${new Date().toISOString()}\n\n`;

    guide += `STEP-BY-STEP PROCESS:\n`;
    guide += `1. Generate UUIDs using this tool\n`;
    guide += `2. Download Excel template with UUIDs\n`;
    guide += `3. Fill in your question content in Excel\n`;
    guide += `4. Name your images using UUID format (if needed)\n`;
    guide += `5. Upload Excel file through Question Management\n\n`;

    guide += `IMAGE NAMING (Optional - only if you have images):\n`;
    guide += `Format: {uuid}_{type}.{extension}\n`;
    guide += `Types: question, option_a, option_b, option_c, option_d, explanation\n\n`;

    if (generatedUUIDs.length > 0) {
      guide += `YOUR GENERATED UUIDS:\n`;
      generatedUUIDs.forEach((item, index) => {
        guide += `Question ${item.questionNumber}: ${item.uuid}\n`;
        if (index < 2) { // Show examples for first 3 questions
          guide += `  Example images: ${item.uuid}_question.jpg, ${item.uuid}_option_a.jpg\n`;
        }
      });
      guide += `\n`;
    }

    guide += `IMPORTANT NOTES:\n`;
    guide += `- Each question must have a unique UUID\n`;
    guide += `- Use the QUESTION_ID column in Excel for your UUIDs\n`;
    guide += `- Images are optional - leave image columns empty if not needed\n`;
    guide += `- Test with 2-3 questions first before bulk upload\n`;

    downloadFile(guide, `bulk_upload_guide_${Date.now()}.txt`);
  };

  // Copy UUID to clipboard
  const copyUUID = async (uuid, index) => {
    try {
      await navigator.clipboard.writeText(uuid);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy UUID:', err);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bulk Upload Assistant v2.0</h1>
        <p className="text-gray-600">
          Simple 3-step process to bulk upload questions with Excel templates.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'workflow', label: 'Step-by-Step Guide', icon: Info },
            { id: 'generator', label: 'Generate UUIDs', icon: Hash },
            { id: 'help', label: 'Help & Tips', icon: HelpCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Step-by-Step Workflow Tab */}
      {activeTab === 'workflow' && (
        <div className="space-y-6">
          <WorkflowGuide />
        </div>
      )}

      {/* UUID Generator Tab */}
      {activeTab === 'generator' && (
        <div className="space-y-6">
          {/* Generator Controls */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Step 1: Generate UUIDs</h2>
            <p className="text-gray-600 mb-6">
              Generate unique IDs for your questions. Each question needs a unique UUID.
            </p>

            <div className="flex items-center space-x-4 mb-6">
              <div>
                <label htmlFor="questionCount" className="block text-sm font-medium text-gray-700 mb-1">
                  How many questions do you want to upload?
                </label>
                <input
                  type="number"
                  id="questionCount"
                  min="1"
                  max="1000"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value) || 1)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <button
                onClick={generateUUIDs}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate UUIDs
              </button>
            </div>

            {generatedUUIDs.length > 0 && (
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    ✅ Generated {generatedUUIDs.length} UUIDs
                  </h3>
                  <div className="flex space-x-3">
                    <button
                      onClick={downloadExcelWithUUIDs}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center text-sm"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Download Excel Template
                    </button>

                    <button
                      onClick={downloadCompleteGuide}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Guide
                    </button>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-green-900 font-medium">Next Steps:</h4>
                      <ol className="text-green-800 text-sm mt-1 list-decimal list-inside space-y-1">
                        <li>Click "Download Excel Template" to get a pre-filled template</li>
                        <li>Fill in your question content in the Excel file</li>
                        <li>Go to Question Management → Bulk Upload to upload your file</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* UUID List - Simplified */}
                <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Your Generated UUIDs:</h4>
                  <div className="grid gap-2">
                    {generatedUUIDs.slice(0, 5).map((item, index) => (
                      <div key={item.uuid} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-500 w-6">#{item.questionNumber}</span>
                          <code className="font-mono text-gray-800 bg-gray-100 px-2 py-1 rounded text-xs">
                            {item.uuid}
                          </code>
                        </div>
                        <button
                          onClick={() => copyUUID(item.uuid, index)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy UUID"
                        >
                          {copiedIndex === index ? (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    ))}
                    {generatedUUIDs.length > 5 && (
                      <div className="text-center text-gray-500 text-sm py-2">
                        ... and {generatedUUIDs.length - 5} more UUIDs
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}



      {/* Help Tab */}
      {activeTab === 'help' && (
        <div className="space-y-6">
          <HelpSection />
        </div>
      )}
    </div>
  );
};

// Workflow Guide Component
const WorkflowGuide = () => (
  <div className="space-y-6">
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
      <h2 className="text-2xl font-bold text-blue-900 mb-4">📚 Simple 3-Step Bulk Upload Process</h2>
      <p className="text-blue-800 mb-4">
        Follow these easy steps to upload multiple questions at once using Excel.
      </p>
    </div>

    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
      {/* Step 1 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 relative">
        <div className="absolute -top-3 -left-3 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg">
          1
        </div>
        <div className="mt-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <Hash className="h-5 w-5 mr-2 text-blue-600" />
            Generate UUIDs
          </h3>
          <p className="text-gray-600 mb-4">
            Create unique IDs for each question you want to upload.
          </p>
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Generate a few extra UUIDs in case you add more questions later.
            </p>
          </div>
        </div>
      </div>

      {/* Step 2 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 relative">
        <div className="absolute -top-3 -left-3 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg">
          2
        </div>
        <div className="mt-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <FileSpreadsheet className="h-5 w-5 mr-2 text-green-600" />
            Fill Excel Template
          </h3>
          <p className="text-gray-600 mb-4">
            Download the Excel template and fill in your question content.
          </p>
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-sm text-green-800">
              ✅ <strong>Easy:</strong> The template comes pre-filled with your UUIDs!
            </p>
          </div>
        </div>
      </div>

      {/* Step 3 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 relative">
        <div className="absolute -top-3 -left-3 bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg">
          3
        </div>
        <div className="mt-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <Upload className="h-5 w-5 mr-2 text-purple-600" />
            Upload & Done!
          </h3>
          <p className="text-gray-600 mb-4">
            Go to Question Management and upload your completed Excel file.
          </p>
          <div className="bg-purple-50 p-3 rounded-lg">
            <p className="text-sm text-purple-800">
              🚀 <strong>Fast:</strong> All questions uploaded in one go!
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <div className="flex items-start">
        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
        <div>
          <h3 className="text-lg font-medium text-yellow-900 mb-2">Quick Start Tips</h3>
          <ul className="list-disc list-inside space-y-1 text-yellow-800">
            <li><strong>Start small:</strong> Try with 2-3 questions first to get familiar</li>
            <li><strong>Images optional:</strong> You can upload questions without images</li>
            <li><strong>Required fields:</strong> Question text, options, correct answer, subject, and topic</li>
            <li><strong>Multiple exam types:</strong> Use commas like "JEE_MAIN,NEET" for multiple exams</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

// Help Section Component
const HelpSection = () => (
  <div className="space-y-6">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <div className="flex items-start">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
        <div>
          <h3 className="text-lg font-medium text-blue-900 mb-2">Excel Template Fields</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Required Fields:</h4>
              <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                <li>QUESTION_ID (your UUID)</li>
                <li>QUESTION_TEXT</li>
                <li>OPTION_A_TEXT, OPTION_B_TEXT, etc.</li>
                <li>CORRECT_ANSWER (1, 2, 3, or 4)</li>
                <li>SUBJECT</li>
                <li>TOPIC</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Optional Fields:</h4>
              <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                <li>SUBTOPIC</li>
                <li>EXAM_TYPES</li>
                <li>DIFFICULTY_LEVEL</li>
                <li>EXPLANATION</li>
                <li>Image columns (if you have images)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
      <div className="flex items-start">
        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
        <div>
          <h3 className="text-lg font-medium text-green-900 mb-2">Image Upload (Optional)</h3>
          <p className="text-green-800 mb-3">
            If you have images for your questions, name them using this format:
          </p>
          <div className="bg-white p-3 rounded border">
            <code className="text-sm text-gray-800">
              {`{your-uuid}_question.jpg`}<br/>
              {`{your-uuid}_option_a.jpg`}<br/>
              {`{your-uuid}_explanation.jpg`}
            </code>
          </div>
          <p className="text-green-800 text-sm mt-2">
            Then reference these filenames in the corresponding image columns in Excel.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-3">Common Questions</h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-gray-800">Q: Can I upload questions without images?</h4>
          <p className="text-gray-600 text-sm">A: Yes! Just leave the image columns empty in the Excel file.</p>
        </div>
        <div>
          <h4 className="font-medium text-gray-800">Q: What if I make a mistake in the Excel file?</h4>
          <p className="text-gray-600 text-sm">A: The system will show you any errors. Fix them and upload again.</p>
        </div>
        <div>
          <h4 className="font-medium text-gray-800">Q: Can I add new subjects or topics?</h4>
          <p className="text-gray-600 text-sm">A: Yes! Just type new values in the Excel file and they'll be created automatically.</p>
        </div>
      </div>
    </div>
  </div>
);

export default UUIDHelper;
